import React, { useState, useEffect, useRef } from 'react';
import {
  Avatar, Box, CircularProgress, Divider, IconButton, ListItemIcon,
  Menu, MenuItem, Tooltip, Typography
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const UserAccountMenu = () => {
  const { user, logout, activeOrg, orgRole, switchToOrg, switchToPersonal } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [switching, setSwitching] = useState(null);
  const fetchedRef = useRef(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open && !fetchedRef.current) {
      fetchedRef.current = true;
      setLoadingOrgs(true);
      api.listMyOrgsPersonal()
        .then(d => setOrgs(d.organisations || []))
        .catch(() => {})
        .finally(() => setLoadingOrgs(false));
    }
  }, [open]);

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    logout();
    // replace: the page we're logging out from is behind PrivateRoute, so a
    // plain push would leave it in history for the back button to bounce
    // off of - see PrivateRoute.js's own comment.
    navigate('/auth', { replace: true });
  };

  const handleSwitchToOrg = async (org) => {
    setSwitching(org.org_id);
    try {
      await switchToOrg(org.org_id);
      handleClose();
      navigate(`/organisations/${org.org_id}`);
    } catch {
      // ignore
    } finally {
      setSwitching(null);
    }
  };

  const handleSwitchToPersonal = () => {
    switchToPersonal();
    handleClose();
    navigate('/dashboard');
  };

  const displayName = activeOrg ? activeOrg.org_name : (user?.customer_name || '');
  const initials = (displayName || '?')[0].toUpperCase();

  return (
    <>
      <Tooltip title="Account">
        <IconButton onClick={handleOpen} size="small" sx={{ ml: 0.5 }}>
          <Avatar
            sx={{
              width: 32, height: 32, fontSize: 14,
              bgcolor: activeOrg ? 'secondary.main' : 'primary.main',
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { minWidth: 240, borderRadius: 2, mt: 0.5 } }}
      >
        {/* Current context label */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {activeOrg ? `Organisation · ${orgRole}` : 'Personal account'}
          </Typography>
          <Typography variant="body2" fontWeight={700} noWrap>
            {displayName}
          </Typography>
        </Box>

        <Divider />

        {/* Personal account option */}
        <MenuItem
          onClick={handleSwitchToPersonal}
          selected={!activeOrg}
          sx={{ gap: 1 }}
        >
          <ListItemIcon>
            {!activeOrg
              ? <CheckCircleIcon fontSize="small" color="primary" />
              : <PersonIcon fontSize="small" />}
          </ListItemIcon>
          <Typography variant="body2">Personal Account</Typography>
        </MenuItem>

        {/* Organisations section */}
        {loadingOrgs ? (
          <MenuItem disabled>
            <CircularProgress size={14} sx={{ mr: 1 }} />
            <Typography variant="body2">Loading orgs…</Typography>
          </MenuItem>
        ) : orgs.length > 0 ? (
          <>
            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Organisations</Typography>
            </Divider>
            {orgs.map((org) => (
              <MenuItem
                key={org.org_id}
                onClick={() => handleSwitchToOrg(org)}
                selected={activeOrg?.org_id === org.org_id}
                sx={{ gap: 1 }}
                disabled={switching === org.org_id}
              >
                <ListItemIcon>
                  {switching === org.org_id
                    ? <CircularProgress size={14} />
                    : activeOrg?.org_id === org.org_id
                      ? <CheckCircleIcon fontSize="small" color="secondary" />
                      : <BusinessIcon fontSize="small" />}
                </ListItemIcon>
                <Box>
                  <Typography variant="body2" noWrap>{org.org_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{org.role}</Typography>
                </Box>
              </MenuItem>
            ))}
          </>
        ) : null}

        <Divider />

        <MenuItem onClick={handleLogout} sx={{ gap: 1, color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserAccountMenu;
